import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { 
  Folder,
  FolderOpen,
  Image,
  Upload,
  Search,
  Plus,
  Trash2,
  Grid3X3,
  List,
  ArrowLeft,
  ArrowRight,
  File,
  Calendar,
  HardDrive,
  RefreshCw,
  ChevronRight,
  Home,
  Eye,
  Info,
  PanelLeft
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase, getAuthenticatedStorage } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import type { 
  ImageFolder, 
  RepositoryImage, 
  ImageFilters, 
  ImageListResponse,
  FolderFormData
} from '@/types/gallery';

// Note: Images are served directly from CDN, no URL transformation needed

// Fetch galleries as "folders" - these represent organized collections of images
const fetchFolders = async (parentId: string | null = null) => {
  // Since we don't have actual folders, we'll use galleries as organizational units
  // For now, we'll return an empty array and focus on showing images directly
  // TODO: We could organize galleries by franchise_region or service_id as "virtual folders"
  
  console.log('Repository Browser - Using galleries as organizational structure');
  
  // We could add virtual folders here in the future:
  // - By franchise region (north_york, hamilton, etc.)
  // - By review status (approved, pending, rejected)  
  // - By service type
  
  return [];
};

// Fetch files from Supabase storage buckets for repository browser
const fetchImages = async (filters: ImageFilters & { page: number; pageSize: number }): Promise<ImageListResponse> => {
  try {
    // Get authenticated storage client
    const { storage } = await getAuthenticatedStorage();
    
    // List files from the main storage bucket (gallery-images)
    const { data: files, error: listError } = await storage
      .from('gallery-images')
      .list('', {
        limit: 1000, // Get all files to enable client-side filtering
        sortBy: { column: 'created_at', order: 'desc' }
      });

    if (listError) {
      console.error('Storage list error:', listError);
      throw listError;
    }

    console.log('Repository Browser - Fetched files from storage bucket:', files?.length || 0);
    
    // Transform storage files to match RepositoryImage interface
    let transformedImages = (files || []).map((file: any, index: number) => {
      // Get public URL for the file
      const { data: urlData } = storage
        .from('gallery-images')
        .getPublicUrl(file.name);

      return {
        id: `storage_${index}_${file.name}`,
        file_name: file.name,
        file_path: urlData.publicUrl,
        file_size: file.metadata?.size || 0,
        mime_type: file.metadata?.mimetype || 'image/jpeg',
        folder_id: null, // Storage files don't have folder_id initially
        description: file.metadata?.description || '',
        metadata: {
          bucket: 'gallery-images',
          ...file.metadata
        },
        created_at: file.created_at || new Date().toISOString(),
        updated_at: file.updated_at || new Date().toISOString()
      };
    });

    // Apply client-side filtering
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      transformedImages = transformedImages.filter(image => 
        image.file_name.toLowerCase().includes(searchLower) ||
        image.description.toLowerCase().includes(searchLower)
      );
    }

    // Apply MIME type filter
    if (filters.mime_type) {
      transformedImages = transformedImages.filter(image => 
        image.mime_type.startsWith(filters.mime_type)
      );
    }

    // Apply pagination
    const totalCount = transformedImages.length;
    const from = (filters.page - 1) * filters.pageSize;
    const to = from + filters.pageSize;
    const paginatedImages = transformedImages.slice(from, to);

    console.log('Repository Browser - After filtering and pagination:', paginatedImages.length, 'of', totalCount);

    return {
      images: paginatedImages,
      totalCount,
      page: filters.page,
      pageSize: filters.pageSize
    };
  } catch (error) {
    console.error('Repository Browser - Storage fetch error:', error);
    throw error;
  }
};

// CRUD operations for folders - Currently disabled since we're using galleries as organization
const createFolder = async (folder: FolderFormData): Promise<ImageFolder> => {
  throw new Error('Folder creation not supported - images are organized by galleries');
};

const updateFolder = async (id: string, updates: Partial<FolderFormData>): Promise<ImageFolder> => {
  throw new Error('Folder updates not supported - images are organized by galleries');
};

const deleteFolder = async (id: string): Promise<void> => {
  throw new Error('Folder deletion not supported - images are organized by galleries');
};

// Image operations for Supabase storage
const deleteImage = async (id: string): Promise<void> => {
  try {
    // Parse the composite ID to get the file name
    const [prefix, index, ...fileNameParts] = id.split('_');
    const fileName = fileNameParts.join('_'); // Rejoin in case filename has underscores
    
    if (prefix === 'storage') {
      const { storage } = await getAuthenticatedStorage();
      
      const { error } = await storage
        .from('gallery-images')
        .remove([fileName]);
      
      if (error) throw error;
      
      console.log('Repository Browser - Deleted file from storage:', fileName);
    } else {
      throw new Error('Invalid file ID format');
    }
  } catch (error) {
    console.error('Repository Browser - Delete error:', error);
    throw error;
  }
};

const moveImage = async (id: string, folderId: string | null): Promise<void> => {
  // Moving images between folders is not supported in the current schema
  // Images are organized by galleries, not folders
  throw new Error('Moving images not supported - images are organized by galleries');
};

// Upload files directly to Supabase storage
const uploadFilesToStorage = async (files: File[]): Promise<void> => {
  const { storage } = await getAuthenticatedStorage();
  
  for (const file of files) {
    // Create unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    // Upload file to Supabase Storage
    const { error } = await storage
      .from('gallery-images')
      .upload(fileName, file);
    
    if (error) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload ${file.name}: ${error.message}`);
    }
    
    console.log('Repository Browser - Uploaded file to storage:', fileName);
  }
};

// Utility functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileTypeIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return Image;
  return File;
};

const getMimeTypeFilter = (type: string) => {
  switch (type) {
    case 'images': return 'image/';
    case 'videos': return 'video/';
    case 'documents': return 'application/';
    case 'all':
    default: return '';
  }
};

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

export default function RepositoryBrowser() {
  // State management
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState<'images'>('images');
  
  // Mobile and responsive states
  const [isMobile, setIsMobile] = useState(false);
  
  // Dialog states (simplified)
  const [isEditFolderOpen, setIsEditFolderOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<ImageFolder | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Filters and search
  const [filters, setFilters] = useState<ImageFilters>({ folder_id: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  
  // Form state (simplified - no folder creation)
  const [folderFormData, setFolderFormData] = useState<FolderFormData>({
    name: '',
    description: ''
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch data - simplified to only fetch images
  const { data: imagesData, isLoading: isImagesLoading, refetch: refetchImages } = useQuery({
    queryKey: ['gallery_images', { searchQuery, fileTypeFilter, currentPage, pageSize }],
    queryFn: () => fetchImages({
      ...filters,
      search: searchQuery,
      mime_type: getMimeTypeFilter(fileTypeFilter),
      page: currentPage,
      pageSize
    })
  });

  // Mutations
  const deleteImageMutation = useMutation({
    mutationFn: deleteImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery_images'] });
      toast({
        title: "Image deleted",
        description: "The image has been successfully deleted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image",
        variant: "destructive",
      });
    }
  });

  const uploadImagesMutation = useMutation({
    mutationFn: uploadFilesToStorage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery_images'] });
      toast({
        title: "Upload successful",
        description: "Files have been uploaded to storage.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload files",
        variant: "destructive",
      });
    }
  });

  // Helper functions (simplified)
  const handleDeleteImage = (id: string) => {
    if (confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      deleteImageMutation.mutate(id);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (activeTab === 'folders') {
      setSelectedItems(new Set(folders.map(f => f.id)));
    } else {
      setSelectedItems(new Set((imagesData?.images || []).map(i => i.id)));
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    uploadImagesMutation.mutate(fileArray);
  };

  // Statistics
  const images = imagesData?.images || [];
  const stats = useMemo(() => {
    const totalImages = imagesData?.totalCount || 0;
    const totalSize = images.reduce((acc, img) => acc + img.file_size, 0);
    const imageTypes = images.reduce((acc, img) => {
      const type = img.mime_type.split('/')[0];
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalFolders: 0, // TODO: Add folder count when folders are implemented
      totalImages,
      totalSize,
      imageTypes,
      selectedCount: selectedItems.size
    };
  }, [imagesData?.totalCount, images, selectedItems.size]);

  const totalPages = Math.ceil((imagesData?.totalCount || 0) / pageSize);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simplified initialization - always show images tab
  useEffect(() => {
    setActiveTab('images');
  }, []);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Repository Browser</h1>
          <p className="text-gray-600 hidden sm:block">Browse and manage files from Supabase storage buckets</p>
        </div>
        
        {!isMobile && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { refetchImages(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            {/* File upload functionality */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadImagesMutation.isPending}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadImagesMutation.isPending ? 'Uploading...' : 'Upload Images'}
            </Button>
          </div>
        )}
        
        {isMobile && (
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Search gallery images..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim() !== '') {
                  setActiveTab('images');
                }
              }}
              className="w-40 sm:w-48"
            />
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Folder className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Folders</p>
                <p className="text-2xl font-bold">{stats.totalFolders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Image className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Images</p>
                <p className="text-2xl font-bold">{stats.totalImages}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <HardDrive className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Storage Used</p>
                <p className="text-2xl font-bold">{formatFileSize(stats.totalSize)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Selected Items</p>
                <p className="text-2xl font-bold">{stats.selectedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breadcrumbs removed - no folder navigation */}

      {/* Filters and Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search files and folders..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.trim() !== '') {
                      setActiveTab('images');
                    }
                  }}
                  className="pl-10 w-64"
                />
              </div>
              
              <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All File Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All File Types</SelectItem>
                  <SelectItem value="images">Images</SelectItem>
                  <SelectItem value="videos">Videos</SelectItem>
                  <SelectItem value="documents">Documents</SelectItem>
                </SelectContent>
              </Select>

              {selectedItems.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{selectedItems.size} selected</Badge>
                  <Button size="sm" variant="outline" onClick={clearSelection}>
                    Clear
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs - Only showing images since folders are not used */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList>
          {/* Folders tab hidden since we organize by galleries */}
          <TabsTrigger value="images">
            <Image className="w-4 h-4 mr-2" />
            Images ({stats.totalImages})
          </TabsTrigger>
        </TabsList>

        {/* Folders tab content removed - not used in this system */}

        <TabsContent value="images" className="mt-6">
          {isImagesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-lg"></div>
              ))}
            </div>
          ) : images.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No images found</h3>
                <p className="text-gray-600 mb-6">
                  No images found in the Supabase storage bucket. Upload images to get started.
                </p>
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Images
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className={cn(
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  : "space-y-2"
              )}>
                {images.map((image) => {
                  const IconComponent = getFileTypeIcon(image.mime_type);
                  
                  return (
                    <Card 
                      key={image.id} 
                      className={cn(
                        "hover:shadow-md transition-shadow cursor-pointer",
                        selectedItems.has(image.id) && "ring-2 ring-blue-500"
                      )}
                      onClick={() => toggleSelection(image.id)}
                    >
                      {viewMode === 'grid' ? (
                        <CardContent className="p-4">
                          <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden relative group">
                            {image.mime_type.startsWith('image/') ? (
                              <img
                                src={image.file_path}
                                alt={image.file_name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center">
                              <IconComponent className="h-12 w-12 text-gray-400" />
                            </div>
                            
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex space-x-2">
                                <Button size="sm" variant="secondary">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Show gallery information instead of move
                                    alert(`Gallery: ${image.metadata?.gallery_title || 'Unknown'}\nRegion: ${image.metadata?.franchise_region || 'Unknown'}\nJob #: ${image.metadata?.job_number || 'N/A'}`);
                                  }}
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteImage(image.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <h3 className="font-medium text-sm truncate">{image.file_name}</h3>
                          <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                            <span>{formatFileSize(image.file_size)}</span>
                            <span>{image.created_at ? new Date(image.created_at).toLocaleDateString() : 'Unknown'}</span>
                          </div>
                        </CardContent>
                      ) : (
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {image.mime_type.startsWith('image/') ? (
                                <img
                                  src={image.file_path}
                                  alt={image.file_name}
                                  className="w-12 h-12 object-cover rounded"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                                  }}
                                />
                              ) : null}
                              <IconComponent className="fallback-icon h-12 w-12 text-gray-400 hidden" />
                              
                              <div>
                                <h3 className="font-medium">{image.file_name}</h3>
                                <p className="text-sm text-gray-600">
                                  {formatFileSize(image.file_size)} • {image.mime_type}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">
                                {image.created_at ? new Date(image.created_at).toLocaleDateString() : 'Unknown'}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  alert(`Gallery: ${image.metadata?.gallery_title || 'Unknown'}\nRegion: ${image.metadata?.franchise_region || 'Unknown'}\nJob #: ${image.metadata?.job_number || 'N/A'}`);
                                }}
                              >
                                <Info className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage(image.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, imagesData?.totalCount || 0)} of {imagesData?.totalCount || 0} images
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Folder Dialog removed - not used in gallery-based system */}

      {/* Move Image Dialog removed - not supported in gallery-based organization */}

      {/* Mobile features removed - no folder structure to navigate */}
    </div>
  );
}